/**
 * WordView — облако слов (kind="word", Фаза 10, И8, §D3.7).
 *
 * `variant="answer"` (игрок): однострочный input слова (maxLength из
 * kindData.maxLen, rune-обрезка на вводе), счётчик «N/maxLen», кнопка
 * «Ответить» (disabled при пустом trim / после отправки); после submit —
 * «Ответ принят»; после reveal — облако WordCloud из reveal.words + строка
 * «Ваше слово: X».
 *
 * `variant="spectate"` (ведущий, паттерн И3): сразу полноценный предпросмотр,
 * не заглушка — до закрытия живое облако из liveWords (событие stats; пока
 * слов нет — «Ждём ответы…»), после reveal — финальное облако. Ввод не
 * предлагается: read-only.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isWordReveal, wordMaxLen, type PlayerAnswer } from '../../protocol';
import type { QuestionBodyProps } from './types';
import { WordCloud } from '../WordCloud';

/** Rune-обрезка ввода по maxLen (по образцу серверной обрезки в движке). */
function clampToMaxLen(value: string, maxLen: number | undefined): string {
  if (maxLen === undefined) return value;
  const runes = Array.from(value);
  return runes.length > maxLen ? runes.slice(0, maxLen).join('') : value;
}

export function WordView({
  question,
  variant,
  myAnswer,
  reveal,
  liveWords,
  participants,
  onAnswer,
}: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const maxLen = wordMaxLen(question);
  const initialWord = myAnswer?.kind === 'word' ? myAnswer.data.word : '';
  const [word, setWord] = useState(initialWord);
  const [submitted, setSubmitted] = useState(myAnswer?.kind === 'word');

  const locked = variant === 'answer' && (submitted || reveal !== null);
  const revealData = reveal?.reveal && isWordReveal(reveal.reveal) ? reveal.reveal : null;

  function handleSubmit(): void {
    if (locked) return;
    const value = word.trim();
    if (!value) return;
    setSubmitted(true);
    const answer: PlayerAnswer = { kind: 'word', data: { word: value } };
    onAnswer(answer);
  }

  // Reveal-бокс общий для игрока и spectate (у ведущего нет своего слова —
  // строка «Ваше слово» не рендерится).
  const revealBox = revealData ? (
    <div className="reveal-box" role="status">
      <WordCloud words={revealData.words} />
      {submitted && variant === 'answer' ? (
        <p className="q-hint" data-testid="word-your">
          {t('word.yourWord', { word: word.trim() })}
        </p>
      ) : null}
    </div>
  ) : null;

  if (variant === 'spectate') {
    // И8: полноценный предпросмотр ведущего — живое облако из stats.words
    // (обновляется по дебаунс-событиям), после reveal — финальное.
    const cloud = revealData ? revealData.words : (liveWords ?? []);
    return (
      <div className="word-view">
        {cloud.length > 0 ? (
          <WordCloud words={cloud} />
        ) : (
          <p className="q-hint" data-testid="word-spectate-waiting">
            {t('word.waiting', { n: participants ?? 0 })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="word-view">
      <input
        className="text-input word-view__input"
        type="text"
        value={word}
        placeholder={t('word.placeholder')}
        maxLength={maxLen}
        disabled={locked}
        onChange={(e) => setWord(clampToMaxLen(e.target.value, maxLen))}
        aria-label={t('word.aria')}
        data-testid="word-answer-input"
      />
      <div className="open-view__row">
        {typeof maxLen === 'number' ? (
          <span className="q-hint small">
            {Array.from(word).length}/{maxLen}
          </span>
        ) : null}
        {submitted && !revealData ? (
          <span className="q-hint q-hint--ok" data-testid="word-answered">
            {t('word.answered')}
          </span>
        ) : null}
        <button
          type="button"
          className="cta open-view__submit"
          disabled={locked || word.trim().length === 0}
          onClick={handleSubmit}
        >
          {t('common.answer')}
        </button>
      </div>

      {revealBox}
    </div>
  );
}
